// src/lib/graderParser.js
export function findBalancedJsonObject(str) {
  if (!str) return null;
  let start = -1, depth = 0, inString = false, escape = false, quote = '';
  for (let i = 0; i < str.length; i++) {
    const c = str[i];
    if (escape) { escape = false; continue; }
    if (inString) {
      if (c === '\\') { escape = true; continue; }
      if (c === quote) { inString = false; continue; }
      continue;
    }
    if (c === '"' || c === "'") { inString = true; quote = c; continue; }
    if (c === '{') { if (depth === 0) start = i; depth++; continue; }
    if (c === '}') { depth--; if (depth === 0 && start !== -1) return str.slice(start, i + 1); }
  }
  return null;
}

function stripFencesAndNoise(text) {
  if (!text) return '';
  let t = String(text);
  t = t.replace(/```json/gi, '```');
  t = t.replace(/```([\s\S]*?)```/g, (m, inner) => inner.trim());
  return t.trim();
}

function parseMaybeFraction(v, cap) {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const m = v.match(/^\s*(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)\s*$/);
    if (m) { const num = parseFloat(m[1]), den = parseFloat(m[2]); if (den > 0) return Math.round((num / den) * cap); }
    const n = parseFloat(v); if (!Number.isNaN(n)) return n;
  }
  return 0;
}

function normalizeParsed(parsed) {
  const breakdownSrc = parsed.breakdown || parsed.scores || {};
  const A = parseMaybeFraction(breakdownSrc.adherence ?? breakdownSrc.Adherence ?? 0, 40);
  const T = parseMaybeFraction(breakdownSrc.technical_sophistication ?? breakdownSrc.technical ?? 0, 30);
  const D = parseMaybeFraction(breakdownSrc.dx ?? breakdownSrc.DX ?? 0, 20);
  const S = parseMaybeFraction(breakdownSrc.contextual_safety ?? breakdownSrc.contextualSafety ?? 0, 10);
  const normalizedBreakdown = {
    adherence: Math.max(0, Math.min(40, Math.round(A))),
    technical_sophistication: Math.max(0, Math.min(30, Math.round(T))),
    dx: Math.max(0, Math.min(20, Math.round(D))),
    contextual_safety: Math.max(0, Math.min(10, Math.round(S))),
  };
  const breakdownSum = normalizedBreakdown.adherence + normalizedBreakdown.technical_sophistication + normalizedBreakdown.dx + normalizedBreakdown.contextual_safety;
  const parsedScore = Number(parsed.score ?? parsed.Score ?? 0) || 0;
  const tolerance = 3;
  const finalScore = (parsedScore && Math.abs(parsedScore - breakdownSum) <= tolerance) ? parsedScore : breakdownSum;
  return {
    score: finalScore,
    parsedScoreIfAny: parsedScore || null,
    breakdownComputedScore: breakdownSum,
    breakdown: normalizedBreakdown,
    metrics: {
      hasConsoleLog: !!(parsed.metrics?.hasConsoleLog ?? parsed.metrics?.consoleLog ?? parsed.metrics?.consoleLogCount),
      hasAria: !!(parsed.metrics?.hasAria),
      todoCount: Number(parsed.metrics?.todoCount ?? parsed.metrics?.todo_count ?? 0)
    },
    hallucinations: Array.isArray(parsed.hallucinations) ? parsed.hallucinations : [],
    reasoning: typeof parsed.reasoning === 'string' ? parsed.reasoning : '',
  };
}

export function parseGraderText(text) {
  if (!text || !String(text).trim()) return { error: 'EMPTY' };
  const cleaned = stripFencesAndNoise(text);
  let candidate = findBalancedJsonObject(cleaned);
  if (!candidate) {
    const fb = cleaned.indexOf('{'), lb = cleaned.lastIndexOf('}');
    if (fb >= 0 && lb > fb) candidate = cleaned.slice(fb, lb + 1);
  }
  if (candidate) {
    try {
      const parsed = JSON.parse(candidate);
      return normalizeParsed(parsed);
    } catch (e) {
      // fallthrough to scavenger
    }
  }
  // scavenger fallback
  const res = { parsedScoreIfAny: null, breakdownComputedScore: 0, breakdown: { adherence: 0, technical_sophistication: 0, dx: 0, contextual_safety: 0 }, score: 0, metrics: { hasConsoleLog: false, hasAria: false, todoCount: 0 }, hallucinations: [], reasoning: '' };
  const scoreMatch = cleaned.match(/Score:\s*(\d+)\s*\/\s*100/i);
  if (scoreMatch) res.parsedScoreIfAny = Number(scoreMatch[1]);
  const adh = cleaned.match(/Adherence:\s*(\d+)\s*\/?\s*40/i);
  if (adh) res.breakdown.adherence = Number(adh[1]);
  const tech = cleaned.match(/Technical\s*Sophistication:\s*(\d+)\s*\/?\s*30/i);
  if (tech) res.breakdown.technical_sophistication = Number(tech[1]);
  const dxm = cleaned.match(/\bDX\b[:\s]*?(\d+)\s*\/?\s*20/i);
  if (dxm) res.breakdown.dx = Number(dxm[1]);
  const cs = cleaned.match(/Contextual\s*Safety:\s*(\d+)\s*\/?\s*10/i);
  if (cs) res.breakdown.contextual_safety = Number(cs[1]);
  res.breakdownComputedScore = res.breakdown.adherence + res.breakdown.technical_sophistication + res.breakdown.dx + res.breakdown.contextual_safety;
  res.score = res.parsedScoreIfAny && Math.abs(res.parsedScoreIfAny - res.breakdownComputedScore) <= 3 ? res.parsedScoreIfAny : res.breakdownComputedScore;
  return res;
}
